import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { detectLocalCurrency } from "@/lib/currency";

let _rates = null;
const loadRates = async () => {
  if (_rates) return _rates;
  const { data } = await api.get("/currencies");
  const symbols = Object.fromEntries((data.currencies || []).map((c) => [c.code, c.symbol]));
  _rates = Object.entries(data.view_rates || {}).map(([code, rate]) => ({ code, rate, symbol: symbols[code] || `${code} ` })).sort((a, b) => a.code.localeCompare(b.code));
  return _rates;
};

export const useConvertedPrice = (amount, currency = "USD") => {
  const [list, setList] = useState(null);
  const [code, setCode] = useState(() => {
    try { return localStorage.getItem("vsk_view_currency") || detectLocalCurrency(); } catch { return ""; }
  });
  useEffect(() => { loadRates().then(setList).catch(() => setList([])); }, []);
  useEffect(() => {
    const h = (e) => setCode(e.detail);
    window.addEventListener("vsk-currency", h);
    return () => window.removeEventListener("vsk-currency", h);
  }, []);
  if (!list || !code || code === currency) return null;
  const from = list.find((c) => c.code === currency);
  const to = list.find((c) => c.code === code);
  if (!from || !to) return null;
  const v = (Number(amount) / from.rate) * to.rate;
  return `${to.symbol}${v.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
};

export const CurrencyFlip = ({ amount, currency = "USD" }) => {
  const [list, setList] = useState(null);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("vsk_view_currency") || detectLocalCurrency(); } catch { return ""; }
  });

  useEffect(() => { loadRates().then(setList).catch(() => setList([])); }, []);
  useEffect(() => {
    const h = (e) => { if (e.detail) setView(e.detail); };
    window.addEventListener("vsk-currency", h);
    return () => window.removeEventListener("vsk-currency", h);
  }, []);

  if (!list || list.length === 0) return null;
  const from = list.find((c) => c.code === currency);
  const to = list.find((c) => c.code === view);
  const converted = from && to && view !== currency
    ? (Number(amount) / from.rate) * to.rate
    : null;

  const change = (code) => {
    setView(code);
    try { localStorage.setItem("vsk_view_currency", code); } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent("vsk-currency", { detail: code })); } catch { /* ignore */ }
  };

  return (
    <div className="mt-1 flex items-center gap-2 text-sm" data-testid="currency-flip">
      <span className="text-neutral-500 text-xs">View in</span>
      <select
        data-testid="currency-flip-select"
        value={view}
        onChange={(e) => change(e.target.value)}
        className="h-7 rounded-md bg-[#121212] border border-[#27272A] text-neutral-300 text-xs px-1.5 outline-none focus:border-[#007AFF]"
      >
        <option value="">—</option>
        {list.filter((c) => c.code !== currency).map((c) => (
          <option key={c.code} value={c.code}>{c.code}</option>
        ))}
      </select>
      {converted != null && (
        <span className="font-mono text-[#FF9F0A]" data-testid="currency-flip-value">
          ≈ {to.symbol}{converted.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
        </span>
      )}
    </div>
  );
};
